/**
 * Chinese Data Service - Unified Data Layer
 * 
 * Provides a unified interface for Chinese learning data with feature flags.
 * Supports gradual migration from Supabase to A2A backend.
 * 
 * Feature Flags:
 * - USE_A2A_BACKEND: Enable A2A backend (default: true)
 * - FALLBACK_TO_SUPABASE: Fallback to Supabase on A2A failure (default: true)
 */

import a2aChineseService from '../api/services/a2aChineseService';

// Import existing Supabase services (if available)
let supabaseKnowledgeGraphService = null;
let supabaseClient = null;

try {
  // Try to import existing Supabase services
  const supabaseModule = require('./supabase/knowledgeGraphService');
  supabaseKnowledgeGraphService = supabaseModule.default || supabaseModule;
} catch (error) {
  console.warn('[ChineseDataService] Supabase knowledge graph service not found');
}

try {
  const supabaseClientModule = require('./supabaseClient');
  supabaseClient = supabaseClientModule.supabase || supabaseClientModule.default;
} catch (error) {
  console.warn('[ChineseDataService] Supabase client not found');
}

/**
 * Feature Flags Configuration
 * Can be overridden via environment variables
 */
const FeatureFlags = {
  USE_A2A_BACKEND: process.env.REACT_APP_USE_A2A_BACKEND !== 'false', // Default: true
  FALLBACK_TO_SUPABASE: process.env.REACT_APP_FALLBACK_TO_SUPABASE !== 'false', // Default: true
  ENABLE_LOGGING: process.env.NODE_ENV === 'development',
};

/**
 * Chinese Data Service Class
 */
class ChineseDataService {
  constructor() {
    this.useA2A = FeatureFlags.USE_A2A_BACKEND;
    this.fallbackToSupabase = FeatureFlags.FALLBACK_TO_SUPABASE;
    this.enableLogging = FeatureFlags.ENABLE_LOGGING;
    this.capabilityCache = new Map();
    
    this.log('ChineseDataService initialized', {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase
    });
  }

  /**
   * Internal logging method
   */
  log(message, data = null) {
    if (this.enableLogging) {
      console.log(`[ChineseDataService] ${message}`, data || '');
    }
  }

  /**
   * Internal error logging method
   */
  logError(message, error) {
    console.error(`[ChineseDataService] ${message}`, error);
  }

  /**
   * Check if A2A backend supports a capability
   * @param {string} capabilityName - Capability name
   * @returns {Promise<boolean>} True if capability is supported
   */
  async checkCapability(capabilityName) {
    if (this.capabilityCache.has(capabilityName)) {
      return this.capabilityCache.get(capabilityName);
    }
    if (!this.useA2A) {
      this.capabilityCache.set(capabilityName, false);
      return false;
    }
    try {
      const hasCap = await a2aChineseService.hasCapability(capabilityName);
      this.capabilityCache.set(capabilityName, hasCap);
      return hasCap;
    } catch (error) {
      this.capabilityCache.set(capabilityName, false);
      return false;
    }
  }

  /**
   * Try A2A with optional Supabase fallback
   */
  async tryWithFallback(a2aMethod, supabaseFallback, methodName) {
    // Try A2A first if enabled
    if (this.useA2A) {
      try {
        this.log(`${methodName}: Using A2A backend`);
        const result = await a2aMethod();
        this.log(`${methodName}: A2A success`);
        return result;
      } catch (error) {
        this.logError(`${methodName}: A2A failed`, error);
        
        // Fallback to Supabase if enabled
        if (this.fallbackToSupabase && supabaseFallback) {
          this.log(`${methodName}: Falling back to Supabase`);
          try {
            const result = await supabaseFallback();
            this.log(`${methodName}: Supabase fallback success`);
            return result;
          } catch (supabaseError) {
            this.logError(`${methodName}: Supabase fallback failed`, supabaseError);
            throw supabaseError;
          }
        }
        
        throw error;
      }
    }
    
    // Use Supabase directly if A2A is disabled
    if (supabaseFallback) {
      this.log(`${methodName}: A2A disabled, using Supabase`);
      return await supabaseFallback();
    }
    
    throw new Error(`${methodName}: A2A disabled and no Supabase fallback available`);
  }

  // ============================================================================
  // Knowledge Graph Methods
  // ============================================================================

  /**
   * Get mock data by story ID (for backward compatibility)
   * @param {string} storyId - Story ID
   * @returns {Promise<Object>} Mock data
   */
  async getMockDataByStoryId(storyId) {
    return await this.tryWithFallback(
      // A2A: Get specific analysis
      async () => {
        const result = await a2aChineseService.getKnowledgeGraphAnalysis(storyId);
        return result.analysis;
      },
      // Supabase fallback
      supabaseKnowledgeGraphService
        ? async () => {
            return await supabaseKnowledgeGraphService.getMockDataByStoryId(storyId);
          }
        : null,
      'getMockDataByStoryId'
    );
  }

  /**
   * Get all sample texts
   * @returns {Promise<Array>} Sample texts
   */
  async getAllSampleTexts() {
    return await this.tryWithFallback(
      // A2A: Get all analyses
      async () => {
        const result = await a2aChineseService.getKnowledgeGraphAnalyses(null, 1, 100);
        return result.analyses || [];
      },
      // Supabase fallback
      supabaseKnowledgeGraphService
        ? async () => {
            return await supabaseKnowledgeGraphService.getAllSampleTexts();
          }
        : null,
      'getAllSampleTexts'
    );
  }

  /**
   * Analyze knowledge graph (A2A only - new feature)
   * @param {string} text - Chinese text to analyze
   * @param {Object} options - Analysis options
   * @returns {Promise<Object>} Knowledge graph analysis
   */
  async analyzeKnowledgeGraph(text, options = {}) {
    if (!this.useA2A) {
      throw new Error('Knowledge graph analysis requires A2A backend');
    }

    this.log('analyzeKnowledgeGraph: Using A2A (no fallback)');
    const result = await a2aChineseService.analyzeKnowledgeGraph(text, options);
    return result;
  }

  /**
   * Save knowledge graph analysis
   * @param {string} sampleName - Sample name
   * @param {string} sampleText - Sample text
   * @param {Object} graphData - Graph data
   * @param {Object} summary - Summary
   * @returns {Promise<Object>} Saved analysis
   */
  async saveKnowledgeGraphAnalysis(sampleName, sampleText, graphData, summary = null) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.saveKnowledgeGraphAnalysis(
          sampleName,
          sampleText,
          graphData,
          summary
        );
      },
      // Supabase fallback
      supabaseKnowledgeGraphService
        ? async () => {
            return await supabaseKnowledgeGraphService.saveMockData({
              sample_name: sampleName,
              sample_text: sampleText,
              graph_nodes: graphData.nodes,
              graph_links: graphData.links,
              graph_summary: summary || {}
            });
          }
        : null,
      'saveKnowledgeGraphAnalysis'
    );
  }

  // ============================================================================
  // Vocabulary Methods
  // ============================================================================

  /**
   * Get vocabulary sets (A2A only)
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Vocabulary sets
   */
  async getVocabularySets(options = {}) {
    if (!this.useA2A) {
      throw new Error('Vocabulary sets feature requires A2A backend');
    }

    this.log('getVocabularySets: Using A2A (no fallback)');
    return await a2aChineseService.getVocabularySets(options);
  }

  /**
   * Get vocabulary set details
   * @param {string} setId - Set ID
   * @returns {Promise<Object>} Vocabulary set
   */
  async getVocabularySet(setId) {
    if (!this.useA2A) {
      throw new Error('Vocabulary set details require A2A backend');
    }

    this.log('getVocabularySet: Using A2A (no fallback)');
    return await a2aChineseService.getVocabularySet(setId);
  }

  /**
   * Update vocabulary progress
   * @param {string} userId - User ID
   * @param {string} setId - Set ID
   * @param {string} phraseId - Phrase ID
   * @param {string} status - Status
   * @param {number} score - Score
   * @returns {Promise<Object>} Updated progress
   */
  async updateVocabularyProgress(userId, setId, phraseId, status, score = null) {
    if (!this.useA2A) {
      throw new Error('Vocabulary progress requires A2A backend');
    }

    this.log('updateVocabularyProgress: Using A2A (no fallback)');
    return await a2aChineseService.updateProgress(userId, setId, phraseId, status, score);
  }

  /**
   * Save vocabulary test result
   * @param {string} userId - User ID
   * @param {string} setId - Set ID
   * @param {number} score - Score
   * @param {number} totalQuestions - Total questions
   * @param {Array} answers - Answers
   * @returns {Promise<Object>} Test result
   */
  async saveVocabularyTestResult(userId, setId, score, totalQuestions, answers) {
    if (!this.useA2A) {
      throw new Error('Vocabulary test results require A2A backend');
    }

    this.log('saveVocabularyTestResult: Using A2A (no fallback)');
    return await a2aChineseService.saveTestResult(userId, setId, score, totalQuestions, answers);
  }

  // ============================================================================
  // Writing Tools Methods
  // ============================================================================

  /**
   * Save composition
   * @param {Object} compositionData - Composition data
   * @returns {Promise<Object>} Saved composition
   */
  async saveComposition(compositionData) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.saveComposition(compositionData);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const { data, error } = await supabaseClient
              .from('chi_writingtools_composition')
              .insert({
                user_id: compositionData.userId,
                title: compositionData.title,
                final_composition_text: compositionData.content,
                style: compositionData.style || 'composition',
                word_length: compositionData.wordLength || 0,
                nodes_data: compositionData.nodesData || [],
                category: compositionData.category || 'AIComposition',
                is_active: true
              })
              .select()
              .single();

            if (error) throw error;
            return { success: true, composition: data };
          }
        : null,
      'saveComposition'
    );
  }

  /**
   * Fetch compositions
   * @param {string} userId - User ID
   * @param {string} category - Category
   * @param {number} page - Page
   * @param {number} limit - Limit
   * @returns {Promise<Object>} Compositions
   */
  async fetchCompositions(userId = null, category = null, page = 1, limit = 20) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.getCompositions(userId, category, page, limit);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            let query = supabaseClient
              .from('chi_writingtools_composition')
              .select('*')
              .eq('is_active', true);

            if (userId) query = query.eq('user_id', userId);
            if (category) query = query.eq('category', category);

            const offset = (page - 1) * limit;
            query = query.range(offset, offset + limit - 1);

            const { data, error } = await query;
            if (error) throw error;

            return {
              success: true,
              compositions: data,
              pagination: { page, limit, total: data.length }
            };
          }
        : null,
      'fetchCompositions'
    );
  }

  /**
   * Get composition by ID
   * @param {string} compositionId - Composition ID
   * @returns {Promise<Object>} Composition
   */
  async getComposition(compositionId) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.getComposition(compositionId);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const { data, error } = await supabaseClient
              .from('chi_writingtools_composition')
              .select('*')
              .eq('id', compositionId)
              .single();

            if (error) throw error;
            return { success: true, composition: data };
          }
        : null,
      'getComposition'
    );
  }

  /**
   * Update composition
   * @param {string} compositionId - Composition ID
   * @param {Object} updates - Updates
   * @returns {Promise<Object>} Updated composition
   */
  async updateComposition(compositionId, updates) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.updateComposition(compositionId, updates);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const updateData = {};
            if (updates.title) updateData.title = updates.title;
            if (updates.content) updateData.final_composition_text = updates.content;
            if (updates.nodesData !== undefined) updateData.nodes_data = updates.nodesData;

            const { data, error } = await supabaseClient
              .from('chi_writingtools_composition')
              .update(updateData)
              .eq('id', compositionId)
              .select()
              .single();

            if (error) throw error;
            return { success: true, composition: data };
          }
        : null,
      'updateComposition'
    );
  }

  /**
   * Delete composition
   * @param {string} compositionId - Composition ID
   * @returns {Promise<Object>} Deletion status
   */
  async deleteComposition(compositionId) {
    return await this.tryWithFallback(
      // A2A
      async () => {
        return await a2aChineseService.deleteComposition(compositionId);
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const { error } = await supabaseClient
              .from('chi_writingtools_composition')
              .update({ is_active: false })
              .eq('id', compositionId);

            if (error) throw error;
            return { success: true, deleted: true };
          }
        : null,
      'deleteComposition'
    );
  }

  // ============================================================================
  // DeepSeek AI Methods (A2A only - new features)
  // ============================================================================

  /**
   * Generate essay (A2A only - new feature)
   * @param {Object} essayConfig - Essay configuration
   * @returns {Promise<Object>} Generated essay
   */
  async generateEssay(essayConfig) {
    if (!this.useA2A) {
      throw new Error('Essay generation requires A2A backend with DeepSeek AI');
    }

    this.log('generateEssay: Using A2A (no fallback)');
    return await a2aChineseService.generateEssay(essayConfig);
  }

  /**
   * Expand sentence (A2A only - new feature)
   * @param {string} keyword - Keyword
   * @param {Object} options - Options
   * @returns {Promise<Object>} Expanded sentence
   */
  async expandSentence(keyword, options = {}) {
    if (!this.useA2A) {
      throw new Error('Sentence expansion requires A2A backend with DeepSeek AI');
    }

    this.log('expandSentence: Using A2A (no fallback)');
    return await a2aChineseService.expandSentence(keyword, options);
  }

  /**
   * Generate drill questions (A2A only - new feature)
   * @param {Object} drillConfig - Drill configuration
   * @returns {Promise<Object>} Generated drills
   */
  async generateDrillQuestions(drillConfig) {
    if (!this.useA2A) {
      throw new Error('Drill generation requires A2A backend with DeepSeek AI');
    }

    this.log('generateDrillQuestions: Using A2A (no fallback)');
    return await a2aChineseService.generateDrillQuestions(drillConfig);
  }

  // ============================================================================
  // UX Negotiation Methods
  // ============================================================================

  /**
   * Negotiate UX format
   * @param {Object} context - User context
   * @returns {Promise<Object>} UX format recommendations
   */
  async negotiateUX(context = null) {
    if (!this.useA2A) {
      this.log('negotiateUX: A2A disabled, returning default UX');
      return this.getDefaultUXFormat(context);
    }

    try {
      this.log('negotiateUX: Using A2A backend');
      return await a2aChineseService.negotiateUX(context);
    } catch (error) {
      this.logError('negotiateUX: A2A failed, returning default', error);
      return this.getDefaultUXFormat(context);
    }
  }

  /**
   * Get default UX format
   * @param {Object} context - User context
   * @returns {Object} Default UX format
   */
  getDefaultUXFormat(context) {
    const detectedContext = context || a2aChineseService.detectContext();
    
    const isMobile = detectedContext.device === 'mobile' || detectedContext.screen_width < 768;
    const isSlowNetwork = ['2g', '3g'].includes(detectedContext.bandwidth);

    return {
      layout: isMobile ? 'cards' : 'table',
      data_granularity: isSlowNetwork ? 'summary' : 'detailed',
      max_items_per_page: isMobile ? 5 : 10,
      include_help: detectedContext.user_level === 'beginner',
      enable_lazy_loading: isSlowNetwork || isMobile,
      font_size: isMobile ? 'large' : 'medium',
      load_images: !isSlowNetwork,
      character_set: detectedContext.preferred_language || 'traditional',
      show_pinyin: detectedContext.user_level === 'beginner',
      language: (detectedContext.preferred_language || 'traditional') === 'traditional' ? 'zh-HK' : 'zh-CN',
    };
  }

  /**
   * Detect user context
   * @returns {Object} User context
   */
  detectContext() {
    return a2aChineseService.detectContext();
  }

  // ============================================================================
  // HKDSE Methods
  // ============================================================================

  /**
   * Get HKDSE texts
   * @param {number} year - Year
   * @param {string} category - Category
   * @param {number} page - Page
   * @param {number} limit - Limit
   * @returns {Promise<Object>} HKDSE texts
   */
  async getHKDSETexts(year = null, category = null, page = 1, limit = 20) {
    if (!this.useA2A) {
      throw new Error('HKDSE texts require A2A backend');
    }

    this.log('getHKDSETexts: Using A2A (no fallback)');
    return await a2aChineseService.getHKDSETexts(year, category, page, limit);
  }

  /**
   * Get HKDSE original text by ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Original text data
   */
  async getHKDSEOriginalText(textId) {
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_original_text');
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      this.log('getHKDSEOriginalText: A2A capability not available, using Supabase directly');
      if (supabaseClient) {
        // Try to use Supabase service if available
        try {
          const { getOriginalTextById } = require('../data/hkdseOriginalTexts');
          return await getOriginalTextById(textId);
        } catch (error) {
          this.logError('getHKDSEOriginalText: Supabase service not available', error);
          throw new Error('Supabase fallback not available');
        }
      }
      throw new Error('Supabase fallback not available');
    }

    return await this.tryWithFallback(
      // A2A: Get original text
      async () => {
        const result = await a2aChineseService.getHKDSEOriginalText(textId);
        // Backend returns: { success: true, data: {...} } or { success: true, data: null }
        if (result && result.success) {
          // Return data (even if null) - component handles null
          return result.data !== undefined ? result.data : null;
        }
        // If result structure is unexpected, return null to trigger fallback
        return null;
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            const { getOriginalTextById } = require('../data/hkdseOriginalTexts');
            return await getOriginalTextById(textId);
          }
        : null,
      'getHKDSEOriginalText'
    );
  }

  /**
   * Get HKDSE quiz by text ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Quiz data
   */
  async getHKDSEQuiz(textId) {
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_quiz');
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      this.log('getHKDSEQuiz: A2A capability not available, using Supabase directly');
      const { getHKDSEQuizByTextId } = require('../data/hkdseQuizData');
      return await getHKDSEQuizByTextId(textId);
    }

    return await this.tryWithFallback(
      // A2A: Get quiz
      async () => {
        const result = await a2aChineseService.getHKDSEQuiz(textId);
        // Backend returns: { success: true, quiz: {...} }
        if (result && result.success && result.quiz) {
          return result.quiz;
        }
        // Fallback: return result as-is if structure is different
        return result;
      },
      // Supabase fallback
      async () => {
        const { getHKDSEQuizByTextId } = require('../data/hkdseQuizData');
        return await getHKDSEQuizByTextId(textId);
      },
      'getHKDSEQuiz'
    );
  }

  /**
   * Get HKDSE flash cards by text ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Flash cards data
   */
  async getHKDSEFlashCards(textId) {
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_flash_cards');
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      this.log('getHKDSEFlashCards: A2A capability not available, using Supabase directly');
      const { getHKDSEFlashCardsByTextId } = require('../data/hkdseFlashCardsData');
      return await getHKDSEFlashCardsByTextId(textId);
    }

    return await this.tryWithFallback(
      // A2A: Get flash cards
      async () => {
        const result = await a2aChineseService.getHKDSEFlashCards(textId);
        // Backend returns: { success: true, flashCards: { id, textId, title, cards: [...] } }
        if (result && result.success && result.flashCards) {
          // Backend now returns the full object structure matching Supabase format
          return result.flashCards;
        }
        // If result structure is unexpected, return null to trigger fallback
        return null;
      },
      // Supabase fallback
      async () => {
        const { getHKDSEFlashCardsByTextId } = require('../data/hkdseFlashCardsData');
        return await getHKDSEFlashCardsByTextId(textId);
      },
      'getHKDSEFlashCards'
    );
  }

  /**
   * Get HKDSE Q&A by text ID (with Supabase fallback)
   * @param {string} textId - Text ID
   * @returns {Promise<Object>} Q&A data
   */
  async getHKDSEQA(textId) {
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_hkdse_qa');
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly (no error thrown)
      this.log('getHKDSEQA: A2A capability not available, using Supabase directly');
      const { getQAByTextId } = require('../data/hkdseQAData');
      return await getQAByTextId(textId);
    }

    return await this.tryWithFallback(
      // A2A: Get Q&A
      async () => {
        const result = await a2aChineseService.getHKDSEQA(textId);
        // Backend returns: { success: true, qa: { title, sections: {...} } }
        if (result && result.success && result.qa) {
          return result.qa;
        }
        // Fallback: return result as-is if structure is different
        return result;
      },
      // Supabase fallback
      async () => {
        const { getQAByTextId } = require('../data/hkdseQAData');
        return await getQAByTextId(textId);
      },
      'getHKDSEQA'
    );
  }

  // ============================================================================
  // Primary Chinese Data Methods
  // ============================================================================

  /**
   * Get primary Chinese data (all grades) - with A2A support and Supabase fallback
   * @returns {Promise<Object>} Primary Chinese data organized by grade
   */
  async getPrimaryChineseData() {
    console.log('[ChineseDataService] getPrimaryChineseData called');
    
    // Check if A2A supports this capability
    const hasCapability = await this.checkCapability('get_primary_chinese_data');
    console.log(`[ChineseDataService] getPrimaryChineseData capability check: ${hasCapability}`);
    
    if (!hasCapability) {
      // Capability not available, use Supabase directly
      console.log('[ChineseDataService] getPrimaryChineseData: A2A capability not available, using Supabase directly');
      if (!supabaseClient) {
        throw new Error('Supabase client not available');
      }

      try {
        console.log('[ChineseDataService] getPrimaryChineseData: Querying Supabase');
        const { data, error } = await supabaseClient
          .from('chi_primarychinese')
          .select('*')
          .order('level', { ascending: true })
          .order('part', { ascending: true });

        if (error) {
          throw error;
        }

        if (!data || data.length === 0) {
          console.warn('[ChineseDataService] getPrimaryChineseData: No data found');
          return null;
        }

        return this.transformPrimaryChineseData(data);
      } catch (error) {
        this.logError('getPrimaryChineseData: Failed to load data', error);
        throw error;
      }
    }

    return await this.tryWithFallback(
      // A2A: Get primary Chinese data
      async () => {
        console.log('[ChineseDataService] getPrimaryChineseData: Calling A2A service');
        const result = await a2aChineseService.getPrimaryChineseData();
        console.log(`[ChineseDataService] getPrimaryChineseData: A2A result:`, {
          hasResult: !!result,
          success: result?.success,
          hasData: !!result?.data
        });
        // Backend returns: { success: true, data: {...} }
        if (result && result.success && result.data) {
          return result.data;
        }
        // If result structure is unexpected, return null to trigger fallback
        console.warn('[ChineseDataService] getPrimaryChineseData: Unexpected result structure, triggering fallback');
        return null;
      },
      // Supabase fallback
      supabaseClient
        ? async () => {
            console.log('[ChineseDataService] getPrimaryChineseData: Using Supabase fallback');
            const { data, error } = await supabaseClient
              .from('chi_primarychinese')
              .select('*')
              .order('level', { ascending: true })
              .order('part', { ascending: true });

            if (error) {
              throw error;
            }

            if (!data || data.length === 0) {
              return null;
            }

            return this.transformPrimaryChineseData(data);
          }
        : null,
      'getPrimaryChineseData'
    );
  }

  /**
   * Transform primary Chinese data from Supabase format to app format
   * @param {Array} data - Raw Supabase records
   * @returns {Object} Transformed and organized data
   */
  transformPrimaryChineseData(data) {
    // Transform and organize data
    const organizedData = {};
    
    data.forEach(record => {
      const level = record.level.toLowerCase(); // P1 -> p1
      const part = record.part.toLowerCase(); // Part1 -> part1, Part2 -> part2
      
      if (!organizedData[level]) {
        organizedData[level] = {};
      }
      
      organizedData[level][part] = {
        title: record.title,
        description: record.description,
        objectives: record.objectives,
        focus: record.focus,
        readingTypes: record.reading_types || [],
        languageUsage: record.language_usage || []
      };
    });

    // Combine Part1 and Part2 for each level
    const combinedData = {};
    
    ['p1', 'p2', 'p3', 'p4', 'p5', 'p6'].forEach(level => {
      const part1Data = organizedData[level]?.part1;
      const part2Data = organizedData[level]?.part2;
      
      if (part1Data || part2Data) {
        // Handle objectives and focus as strings (they are TEXT in database)
        const objectives1 = part1Data?.objectives || '';
        const objectives2 = part2Data?.objectives || '';
        const combinedObjectives = [objectives1, objectives2]
          .filter(s => s && typeof s === 'string' && s.trim())
          .join(' ');
        
        const focus1 = part1Data?.focus || '';
        const focus2 = part2Data?.focus || '';
        const combinedFocus = [focus1, focus2]
          .filter(s => s && typeof s === 'string' && s.trim())
          .join(' ');
        
        combinedData[level] = {
          title: part1Data?.title || part2Data?.title || '',
          description: part1Data?.description || part2Data?.description || '',
          objectives: combinedObjectives || (part1Data?.objectives || part2Data?.objectives || ''),
          focus: combinedFocus || (part1Data?.focus || part2Data?.focus || ''),
          readingTypes: [
            ...(Array.isArray(part1Data?.readingTypes) ? part1Data.readingTypes : []),
            ...(Array.isArray(part2Data?.readingTypes) ? part2Data.readingTypes : [])
          ],
          languageUsage: [
            ...(Array.isArray(part1Data?.languageUsage) ? part1Data.languageUsage : []),
            ...(Array.isArray(part2Data?.languageUsage) ? part2Data.languageUsage : [])
          ]
        };
      }
    });

    return combinedData;
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Get feature flags status
   * @returns {Object} Feature flags
   */
  getFeatureFlags() {
    return {
      useA2A: this.useA2A,
      fallbackToSupabase: this.fallbackToSupabase,
      enableLogging: this.enableLogging
    };
  }

  /**
   * Set feature flag
   * @param {string} flag - Flag name
   * @param {boolean} value - Flag value
   */
  setFeatureFlag(flag, value) {
    if (flag === 'useA2A') {
      this.useA2A = value;
      this.log(`Feature flag updated: useA2A = ${value}`);
    } else if (flag === 'fallbackToSupabase') {
      this.fallbackToSupabase = value;
      this.log(`Feature flag updated: fallbackToSupabase = ${value}`);
    } else if (flag === 'enableLogging') {
      this.enableLogging = value;
    }
  }

  /**
   * Get service status
   * @returns {Object} Service status
   */
  async getServiceStatus() {
    const status = {
      a2a: {
        enabled: this.useA2A,
        available: false,
        capabilities: 0
      },
      supabase: {
        fallbackEnabled: this.fallbackToSupabase,
        available: supabaseClient !== null
      }
    };

    if (this.useA2A) {
      try {
        const capabilities = a2aChineseService.getCapabilities();
        if (capabilities) {
          status.a2a.available = true;
          status.a2a.capabilities = capabilities.length;
        }
      } catch (error) {
        this.logError('Failed to get A2A status', error);
      }
    }

    return status;
  }

  /**
   * Cleanup - call when unmounting app
   */
  cleanup() {
    this.log('Cleaning up ChineseDataService');
    a2aChineseService.cleanup();
  }
}

/**
 * Create and export singleton instance
 */
const chineseDataService = new ChineseDataService();

export default chineseDataService;

/**
 * Export class for testing or custom instances
 */
export { ChineseDataService, FeatureFlags };

